/**
 * Mobile Menu JavaScript
 * Handles mobile menu interactions and slide navigation
 */

class MobileMenu {
  constructor() {
    this.overlay = document.getElementById('mobile-menu-overlay');
    this.toggle = document.getElementById('mobile-menu-toggle');
    this.close = document.getElementById('mobile-menu-close');
    this.back = document.getElementById('mobile-menu-back');
    this.level1 = document.getElementById('mobile-menu-level-1');
    this.level2 = document.getElementById('mobile-menu-level-2');
    this.submenuTitle = document.getElementById('mobile-submenu-title');
    this.submenuContent = document.getElementById('mobile-submenu-content');

    this.isOpen = false;
    this.currentLevel = 1;
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Get menu data from global variable (set by PHP)
    this.menuData = this.buildMenuDataFromGlobal();

    this.init();
  }

  buildMenuDataFromGlobal() {
    // Convert PHP menu data to JavaScript format
    const menuData = {};

    // Check if global data exists
    if (typeof window.mobileMenuData === 'undefined') {
      console.warn('Mobile menu data not found. Please ensure the mobile menu template is loaded.');
      return {};
    }

    // Process primary menu items
    window.mobileMenuData.primary.forEach((item) => {
      if (item.has_submenu && item.submenu) {
        menuData[item.label] = item.submenu;
      }
    });

    // Process secondary menu items
    window.mobileMenuData.secondary.forEach((item) => {
      if (item.submenu) {
        menuData[item.label] = item.submenu;
      }
    });

    return menuData;
  }

  init() {
    this.bindEvents();
    this.setupSubmenuButtons();
  }

  bindEvents() {
    // Toggle menu
    this.toggle?.addEventListener('click', () => this.toggleMenu());

    // Close menu
    this.close?.addEventListener('click', () => this.closeMenu());

    // Back button
    this.back?.addEventListener('click', () => this.goBack());

    // Overlay click to close
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeMenu();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
    });

    // Prevent body scroll when menu is open
    this.overlay?.addEventListener('wheel', (e) => {
      if (this.isOpen) {
        e.preventDefault();
      }
    });

    // Touch gestures for swipe navigation
    this.setupTouchGestures();
  }

  setupSubmenuButtons() {
    const submenuButtons = document.querySelectorAll('[data-submenu]');
    submenuButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const submenuName = button.getAttribute('data-submenu');
        this.showSubmenu(submenuName);
      });
    });
  }

  toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.isOpen = true;
    this.overlay?.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    this.toggle?.setAttribute('aria-expanded', 'true');

    // Reset to level 1
    this.currentLevel = 1;
    this.level1?.classList.add('active');
    this.level2?.classList.remove('active');

    // Animate hamburger icon
    this.animateHamburger(true);
  }

  closeMenu() {
    this.isOpen = false;
    this.overlay?.classList.add('hidden');
    document.body.style.overflow = '';
    this.toggle?.setAttribute('aria-expanded', 'false');

    // Reset to level 1
    this.currentLevel = 1;
    this.level1?.classList.add('active');
    this.level2?.classList.remove('active');

    // Animate hamburger icon
    this.animateHamburger(false);
  }

  showSubmenu(submenuName) {
    if (this.currentLevel === 1) {
      this.level1?.classList.remove('active');
      this.level1?.classList.add('prev');
      this.level2?.classList.add('active');
      this.currentLevel = 2;

      // Update submenu title
      this.submenuTitle.textContent = submenuName;

      // Populate submenu content
      this.populateSubmenu(submenuName);
    }
  }

  goBack() {
    if (this.currentLevel === 2) {
      this.level2?.classList.remove('active');
      this.level1?.classList.remove('prev');
      this.level1?.classList.add('active');
      this.currentLevel = 1;
    }
  }

  populateSubmenu(submenuName) {
    const data = this.menuData[submenuName];
    if (!data) return;

    let html = '';

    if (Array.isArray(data)) {
      // Check if it's a simple array of links or grouped data
      if (data[0] && data[0].heading) {
        // Grouped data
        data.forEach((group) => {
          html += `<div class="mobile-submenu-group">`;
          if (group.heading) {
            html += `<h4 class="mobile-submenu-group-title">${group.heading}</h4>`;
          }
          group.links.forEach((link) => {
            html += `<a href="${link.url}" class="mobile-submenu-item">${link.label}</a>`;
          });
          html += `</div>`;
        });
      } else {
        // Simple array of links
        data.forEach((link) => {
          html += `<a href="${link.url}" class="mobile-submenu-item">${link.label}</a>`;
        });
      }
    }

    this.submenuContent.innerHTML = html;
  }

  setupTouchGestures() {
    // Touch events for swipe gestures
    this.overlay?.addEventListener(
      'touchstart',
      (e) => {
        this.touchStartX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.overlay?.addEventListener(
      'touchend',
      (e) => {
        this.touchEndX = e.changedTouches[0].clientX;
        this.handleSwipe();
      },
      { passive: true }
    );
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const swipeDistance = this.touchEndX - this.touchStartX;

    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0 && this.currentLevel === 2) {
        // Swipe right to go back
        this.goBack();
      } else if (swipeDistance < 0 && this.currentLevel === 1) {
        // Swipe left to close menu (only if on level 1)
        this.closeMenu();
      }
    }
  }

  animateHamburger(isOpen) {
    const icon = this.toggle?.querySelector('.mobile-menu-icon');
    if (!icon) return;

    const spans = icon.querySelectorAll('span');

    if (isOpen) {
      // Transform to X
      spans[0].style.transform = 'rotate(45deg) translate(5px, 5px)';
      spans[1].style.opacity = '0';
      spans[2].style.transform = 'rotate(-45deg) translate(7px, -6px)';
    } else {
      // Reset to hamburger
      spans[0].style.transform = '';
      spans[1].style.opacity = '';
      spans[2].style.transform = '';
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
});
